import React, { useState, useEffect, useContext } from 'react';
import { Box, Fade, Alert } from '@mui/material';
import { ConfigContext } from '../shared/ConfigContext';
import AdvertisementClient from '../../api/advertisementClient';
import AdvertisementRenderer from '../shared/AdvertisementRenderer';

const DISMISSED_ADS_KEY = 'dismissedAdvertisements';

/**
 * Container component that fetches and displays active advertisements for users
 * Shows one ad at a time - when dismissed, the next priority ad is shown
 * Dismissed ads are stored in sessionStorage so they reset on each login session
 */
const AdvertisementsContainer = () => {
  const [allAds, setAllAds] = useState([]); // All active ads from backend
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dismissedAds, setDismissedAds] = useState(() => {
    try {
      // Use sessionStorage so ads show again on each new login session
      const stored = sessionStorage.getItem(DISMISSED_ADS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const config = useContext(ConfigContext);

  const fetchActiveAdvertisements = async () => {
    try {
      setLoading(true);
      const client = new AdvertisementClient(config.backendUrl);
      const response = await client.getActiveAdvertisements();
      
      // Sort by priority (highest first) and store all ads
      const ads = (response.data || [])
        .sort((a, b) => (b.priority || 0) - (a.priority || 0));
      
      setAllAds(ads);
      setError('');
    } catch (err) {
      console.error('Error fetching advertisements:', err);
      setError('');
      // Silently fail - advertisements are not critical to user experience
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (config?.backendUrl) {
      fetchActiveAdvertisements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // Get the first non-dismissed ad to show
  const currentAd = allAds.find(ad => !dismissedAds.includes(ad._id || ad.id));

  const handleDismiss = async (adId) => {
    try {
      // Update dismissed list in sessionStorage (resets each login session)
      const newDismissedAds = [...dismissedAds, adId];
      setDismissedAds(newDismissedAds);
      sessionStorage.setItem(DISMISSED_ADS_KEY, JSON.stringify(newDismissedAds));
      
      // Notify backend
      const client = new AdvertisementClient(config.backendUrl);
      await client.dismissAdvertisement(adId);
    } catch (err) {
      console.error('Error dismissing advertisement:', err);
      // Don't show error to user - the ad is already removed from UI
    }
  };

  if (loading || !currentAd) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      {error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Fade in={true} key={currentAd._id || currentAd.id} timeout={300}>
        <Box>
          <AdvertisementRenderer
            advertisement={currentAd}
            onDismiss={handleDismiss}
            variant="banner"
          />
        </Box>
      </Fade>
    </Box>
  );
};

export default AdvertisementsContainer;
